import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { useProductFavoritesStore } from '@/stores/productFavoritesStore'
import { isPlatformAdmin } from '@/lib/dashboardAccess'
import { authService } from '@/services/authService'

interface User {
  id: number
  email: string
  name: string
  role?: string
  shop_id?: number
  shop_name?: string
  shop_code?: string
  is_web_user?: boolean
  customer_code?: string
  branch_access?: 'all' | number[]
  primary_branch_id?: number | null
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  expiresIn: number | null
  idleTimeoutMinutes: number | null
  maxSessionExpiresAt: string | null
}

interface AuthActions {
  login: (token: string, user: User) => void
  applySession: (data: {
    token: string
    user: User
    expires_in?: number
    idle_timeout_minutes?: number
    max_session_expires_at?: string
  }) => void
  logout: () => void
  performLogout: () => Promise<void>
  setLoading: (loading: boolean) => void
  updateUser: (userData: Partial<User>) => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      expiresIn: null,
      idleTimeoutMinutes: null,
      maxSessionExpiresAt: null,

      // Actions
      login: (token: string, user: User) => {
        if (token) {
          localStorage.setItem('auth-token', token)
        }
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
        })
      },

      applySession: (data) => {
        if (data.token) {
          localStorage.setItem('auth-token', data.token)
        }
        set({
          token: data.token,
          user: data.user,
          isAuthenticated: true,
          isLoading: false,
          expiresIn: data.expires_in ?? null,
          idleTimeoutMinutes: data.idle_timeout_minutes ?? null,
          maxSessionExpiresAt: data.max_session_expires_at ?? null,
        })
      },

      logout: () => {
        usePermissionsStore.getState().clear()
        useProductFavoritesStore.getState().clear()
        sessionStorage.removeItem('last-activity-at')
        sessionStorage.removeItem('oms-last-activity-at')
        try {
          localStorage.removeItem('oms-last-activity-at')
        } catch {
          /* ignore */
        }
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          expiresIn: null,
          idleTimeoutMinutes: null,
          maxSessionExpiresAt: null,
        })
        localStorage.removeItem('auth-token')
      },

      performLogout: async () => {
        const { token, isAuthenticated, logout } = get()
        if (isAuthenticated && (token || localStorage.getItem('auth-token'))) {
          try {
            await authService.logout()
          } catch {
            /* still clear local state if server is unreachable */
          }
        }
        logout()
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData }
          })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        expiresIn: state.expiresIn,
        idleTimeoutMinutes: state.idleTimeoutMinutes,
        maxSessionExpiresAt: state.maxSessionExpiresAt,
      }),
    }
  )
)

// Helper functions
export const getAuthToken = () => {
  const state = useAuthStore.getState()
  return state.token
}

export const isStaff = () => {
  const state = useAuthStore.getState()
  return state.user && !state.user.is_web_user
}

export const isCustomer = () => {
  const state = useAuthStore.getState()
  return state.user && state.user.is_web_user
}

/** Shop admin or platform super_admin (matches server AuthUtils.isAdmin / requireAdmin). */
export const isAdmin = () => isPlatformAdmin(useAuthStore.getState().user?.role)

/** Platform administrator (oms-wide); sees Platform admin UI and all shops for quota. */
export const isSuperAdmin = () => {
  const state = useAuthStore.getState()
  return state.user?.role === 'super_admin'
}

export const isManagerOrAdmin = () => {
  const state = useAuthStore.getState()
  const r = state.user?.role
  return r === 'super_admin' || r === 'admin' || r === 'manager'
}

export const isSeniorStaffOrAbove = () => {
  const state = useAuthStore.getState()
  return (
    state.user?.role === 'super_admin' ||
    state.user?.role === 'admin' ||
    state.user?.role === 'manager' ||
    state.user?.role === 'senior_staff'
  )
}

/** Shop settings: read-only quota card (not for platform super_admin). */
export const canViewShopQuotaReadonly = () => {
  const u = useAuthStore.getState().user
  if (!u?.shop_id || u.role === 'super_admin') return false
  return u.role === 'admin' || u.role === 'manager' || u.role === 'senior_staff'
}

export const getCurrentShopId = () => {
  const state = useAuthStore.getState()
  return state.user?.shop_id
}
